import { readDelegationProgressData, type DelegationProgressView } from './delegation-progress';

/**
 * FRONT-3211: chat tasks as the composer's chip row shows them. The wire
 * shape of `GET /api/chat/:sessionId/tasks` and the pure decisions the chips,
 * the panel and the composer make from it. Dependency-free (no React, no
 * query client), so the frontend's static-markup suite can pin every state.
 */

export type ChatTaskStatus =
  | 'waiting_to_start'
  | 'running'
  | 'needs_approval'
  | 'done'
  | 'partial'
  | 'failed'
  | 'refused'
  | 'interrupted'
  | 'cancelled';

export interface ChatTaskAgent {
  id: string;
  toolCallId: string;
  agentId: string;
  agentLabel: string;
  status: ChatTaskStatus;
  chainedAfterAgentId: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface ChatTask {
  id: string;
  sessionId: string;
  title: string;
  status: ChatTaskStatus;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  deliveredAt: string | null;
  agents: ChatTaskAgent[];
}

/** Live progress per agent row id (frames arrive over the session channel only). */
export type ChatTaskProgress = Record<string, DelegationProgressView>;

const SETTLED: ReadonlySet<string> = new Set(['done', 'partial', 'failed', 'refused', 'interrupted', 'cancelled']);

export function isSettledTaskStatus(status: string): boolean {
  return SETTLED.has(status);
}

/** How long a delivered task's chip keeps reading "done" before it goes. */
export const DONE_LINGER_MS = 2_500;

/** `m:ss` since `fromIso`, like the mockup's timer; empty for no start. */
export function formatTaskClock(fromIso: string | null, now: number): string {
  if (!fromIso) return '';
  const from = Date.parse(fromIso);
  if (!Number.isFinite(from)) return '';
  const s = Math.max(0, Math.floor((now - from) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export type TaskChipTone = 'working' | 'needs-you' | 'waiting' | 'done' | 'ended';

export interface TaskChipView {
  visible: boolean;
  tone: TaskChipTone;
  /** The right-hand status: a live clock, "needs you", "done", … */
  status: string;
  /** True while `status` is a ticking clock (tabular figures, live region quiet). */
  isClock: boolean;
  agentCount: string;
}

const ENDED_WORDS: Partial<Record<ChatTaskStatus, string>> = {
  failed: 'failed',
  refused: 'refused',
  interrupted: 'interrupted',
};

/**
 * One chip. A stopped task leaves at once (its note is in the thread); a
 * delivered one reads "done" for {@link DONE_LINGER_MS}, counted from the
 * EARLIER of the server's delivery stamp and the moment this tab first saw it
 * delivered — so a reload long after delivery shows nothing, and a client
 * clock behind the server's cannot keep a chip up.
 */
export function taskChipView(task: ChatTask, now: number, firstSeenDeliveredMs?: number): TaskChipView {
  const agentCount = `${task.agents.length} agent${task.agents.length === 1 ? '' : 's'}`;
  const base = { agentCount, isClock: false };
  if (task.status === 'cancelled') return { ...base, visible: false, tone: 'ended', status: 'stopped' };
  if (task.deliveredAt) {
    const stamped = Date.parse(task.deliveredAt);
    const deliveredMs = Math.min(Number.isFinite(stamped) ? stamped : now, firstSeenDeliveredMs ?? now);
    if (now - deliveredMs >= DONE_LINGER_MS) return { ...base, visible: false, tone: 'done', status: 'done' };
  }
  switch (task.status) {
    case 'waiting_to_start':
      return { ...base, visible: true, tone: 'waiting', status: 'waiting to start' };
    case 'needs_approval':
      return { ...base, visible: true, tone: 'needs-you', status: 'needs you' };
    case 'running':
      return { ...base, visible: true, tone: 'working', status: formatTaskClock(task.startedAt ?? task.createdAt, now), isClock: true };
    case 'done':
    case 'partial':
      return { ...base, visible: true, tone: 'done', status: 'done' };
    default:
      return { ...base, visible: true, tone: 'ended', status: ENDED_WORDS[task.status] ?? task.status };
  }
}

/**
 * The open panel's task id while its chip is still showing, else null. A
 * panel whose chip left (delivered, stopped in another tab, dropped by a
 * refetch) is closed, so nothing it listens for outlives it.
 */
export function openTaskIfVisible(
  tasks: readonly ChatTask[],
  openTaskId: string | null,
  now: number,
  firstSeenDelivered?: ReadonlyMap<string, number>,
): string | null {
  if (!openTaskId) return null;
  const task = tasks.find((t) => t.id === openTaskId);
  return task && taskChipView(task, now, firstSeenDelivered?.get(task.id)).visible ? openTaskId : null;
}

/** One agent's right-hand status in the panel. */
export function agentStatusText(agent: ChatTaskAgent, task: ChatTask, now: number): string {
  switch (agent.status) {
    case 'running':
      return formatTaskClock(agent.startedAt, now) || 'working';
    case 'needs_approval':
      return 'waiting on you';
    case 'waiting_to_start': {
      const after = agent.chainedAfterAgentId ? task.agents.find((a) => a.id === agent.chainedAfterAgentId) : undefined;
      return after ? `after ${after.agentLabel}` : 'waiting to start';
    }
    case 'done':
      return 'done';
    case 'partial':
      return 'partly done';
    case 'cancelled':
      return 'stopped';
    default:
      return agent.status;
  }
}

/**
 * A task settled but its result has not reached the chat yet: its result turn
 * is queued or running. A send typed meanwhile must wait in the composer's
 * queue, so the results land before it (spec: results go before queued user
 * messages). A stopped task delivers its note at once, so it never counts.
 */
export function isTaskResultPending(tasks: readonly ChatTask[] | undefined, released?: ReadonlySet<string>): boolean {
  return pendingResultTaskIds(tasks).some((id) => !released?.has(id));
}

/**
 * The tasks whose result is on its way (see {@link isTaskResultPending}).
 * The composer's Stop releases the queue from exactly these: a stopped
 * reply, result turn or not, frees a queued draft at once, like any stopped
 * reply; a task that settles after that Stop holds the queue again.
 */
export function pendingResultTaskIds(tasks: readonly ChatTask[] | undefined): string[] {
  return (tasks ?? []).filter((t) => isSettledTaskStatus(t.status) && t.status !== 'cancelled' && !t.deliveredAt).map((t) => t.id);
}

/** Fold a `chat-task-progress` frame into the per-agent map; unchanged on a malformed frame. */
export function applyTaskProgressFrame(prev: ChatTaskProgress | undefined, payload: Record<string, unknown>): ChatTaskProgress {
  const map = prev ?? {};
  const agentId = typeof payload.agentId === 'string' ? payload.agentId : '';
  if (!agentId) return map;
  const view = readDelegationProgressData(payload.progress);
  if (!view) return map;
  return { ...map, [agentId]: view };
}

/**
 * Fold a `chat-task-changed` event into the cached list so the chip moves
 * at once; the list is refetched right after, which stays the truth. An
 * unknown task is left to that refetch.
 */
export function applyTaskChangedEvent(prev: ChatTask[] | undefined, payload: Record<string, unknown>, nowIso: string): ChatTask[] | undefined {
  if (!prev) return prev;
  const taskId = typeof payload.taskId === 'string' ? payload.taskId : '';
  if (!taskId) return prev;
  const status = typeof payload.status === 'string' ? (payload.status as ChatTaskStatus) : undefined;
  const agentId = typeof payload.agentId === 'string' ? payload.agentId : undefined;
  const agentStatus = typeof payload.agentStatus === 'string' ? (payload.agentStatus as ChatTaskStatus) : undefined;
  const delivered = payload.delivered === true;
  return prev.map((t) => {
    if (t.id !== taskId) return t;
    return {
      ...t,
      ...(status ? { status } : {}),
      ...(status && isSettledTaskStatus(status) && !t.finishedAt ? { finishedAt: nowIso } : {}),
      ...(delivered && !t.deliveredAt ? { deliveredAt: nowIso } : {}),
      agents:
        agentId && agentStatus ? t.agents.map((a) => (a.id === agentId ? { ...a, status: agentStatus } : a)) : t.agents,
    };
  });
}

/**
 * FRONT-3263: the cached job lists (one per chat) that are loaded and do not
 * hold `taskId`. A "moved to job" card names a job the chip row may never have
 * heard of: its start event can be lost (the browser's event stream runs on
 * another server than the turn, or the chat's channel had no listener yet), so
 * the card itself asks for these lists to be re-read. A list still loading is
 * left to its own first fetch.
 */
export function listsMissingTask<K>(
  lists: ReadonlyArray<readonly [K, readonly ChatTask[] | undefined]>,
  taskId: string,
): K[] {
  return lists.filter(([, tasks]) => tasks !== undefined && !tasks.some((t) => t.id === taskId)).map(([key]) => key);
}

/**
 * FRONT-3263: which "moved to job" card chases its job: only one that this
 * page watched turn from running into the hand-off. A card loaded from
 * history is for a job the list has already reported, or one too old to be
 * in it at all (the list keeps a delivered job only 10 minutes), and chasing
 * that would re-read the list for nothing on every chat open.
 */
export function watchedJobId(
  branched: { taskId: string } | null,
  mountedRunning: boolean,
): string | null {
  return branched && mountedRunning ? branched.taskId : null;
}

/**
 * FRONT-3263: jobs the previous list showed undelivered and the new one shows
 * delivered. A re-read found the delivery without its event, so the chat's
 * history must be re-read before the list moves on (the same order the
 * delivered event keeps: the answer is on screen before a held send goes).
 */
export function newlyDeliveredTaskIds(prev: readonly ChatTask[] | undefined, next: readonly ChatTask[]): string[] {
  const undelivered = new Set((prev ?? []).filter((t) => !t.deliveredAt).map((t) => t.id));
  return next.filter((t) => t.deliveredAt && undelivered.has(t.id)).map((t) => t.id);
}

/**
 * The branched `delegate_to_agent` tool result (src/agents/chat-tasks/runner.ts:
 * `branchedNote` / `queuedNote` / `joinedNote`): the call left the turn and
 * now runs as a chat task. Null for every other result.
 */
/** The one-line card summary for a delegation that left the turn as a job. */
export function branchedSummary(b: { title: string; queued: boolean; joined: boolean }): string {
  if (b.queued) return `waiting to start as job ${b.title}`;
  if (b.joined) return `added to job ${b.title}`;
  return `moved to job ${b.title}`;
}

export function readBranchedDelegation(output: unknown): { taskId: string; title: string; queued: boolean; joined: boolean } | null {
  let o = output;
  if (typeof o === 'string') {
    try {
      o = JSON.parse(o);
    } catch {
      return null;
    }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  const r = o as Record<string, unknown>;
  if (r.branched !== true || typeof r.taskId !== 'string') return null;
  const note = typeof r.note === 'string' ? r.note : '';
  const title =
    typeof r.taskTitle === 'string' && r.taskTitle.trim()
      ? r.taskTitle.trim()
      : (/(?:task|job) '([^']+)'/.exec(note)?.[1] ?? 'a background job');
  return { taskId: r.taskId, title, queued: r.queued === true, joined: r.joined === true };
}
